import { useEffect, useId, useRef, useState } from 'react';
import type { MessageTemplate } from '../../../../lib/shared/types';
import { useI18n } from '../../contexts/I18nContext';
import {
  defaultMessageTemplate,
  messageVariables,
  renderMessage,
  validMessageTemplate,
} from './messageText';

export function MessageTemplateEditor({
  initialValue,
  sender,
  onSave,
  onCancel,
}: {
  initialValue: MessageTemplate;
  sender: string;
  onSave: (value: MessageTemplate) => Promise<boolean>;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const id = useId();
  const input = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    input.current?.focus();
  }, []);
  const [draft, setDraft] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const valid = validMessageTemplate(draft);
  const preview = renderMessage(draft, 'Thorben', {
    domain: 'service',
    serviceRole: 'AttendantEntrance',
    source: 'released',
    date: `${new Date().getFullYear()}-08-01`,
  });
  return (
    <form
      className="space-y-4"
      onSubmit={async event => {
        event.preventDefault();
        if (!valid || saving) return;
        setSaving(true);
        setFailed(false);
        if (await onSave(draft)) onCancel();
        else {
          setSaving(false);
          setFailed(true);
        }
      }}
    >
      <div>
        <label className="block text-sm font-medium" htmlFor={`${id}-language`}>
          {t('message.language')}
        </label>
        <select
          id={`${id}-language`}
          className="input-field mt-1 w-full min-h-11"
          value={draft.language}
          disabled={saving}
          onChange={event =>
            setDraft({ ...draft, language: event.target.value as MessageTemplate['language'] })
          }
        >
          <option value="de">{t('message.german')}</option>
          <option value="en">{t('message.english')}</option>
        </select>
        <p className="mt-1 text-sm text-slate-600">{t('message.languageHelp')}</p>
      </div>
      <div>
        <label className="block text-sm font-medium" htmlFor={`${id}-text`}>
          {t('message.template')}
        </label>
        <textarea
          ref={input}
          id={`${id}-text`}
          data-autofocus
          rows={6}
          className="input-field mt-1 w-full"
          value={draft.text}
          disabled={saving}
          aria-invalid={!valid}
          aria-describedby={!valid ? `${id}-invalid` : undefined}
          onChange={event => {
            setDraft({ ...draft, text: event.target.value });
            setFailed(false);
          }}
        />
        <div className="mt-2 flex flex-wrap gap-2" aria-label={t('message.variables')}>
          {messageVariables.map(variable => (
            <button
              key={variable}
              type="button"
              className="btn-secondary min-h-11 text-sm"
              disabled={saving}
              onClick={() => {
                const field = input.current;
                const start = field?.selectionStart ?? draft.text.length;
                const end = field?.selectionEnd ?? start;
                const token = `{{${variable}}}`;
                setDraft({
                  ...draft,
                  text: draft.text.slice(0, start) + token + draft.text.slice(end),
                });
                requestAnimationFrame(() => {
                  field?.focus();
                  field?.setSelectionRange(start + token.length, start + token.length);
                });
              }}
            >
              {t(`message.variable.${variable}`)}
            </button>
          ))}
        </div>
        {!valid && (
          <p id={`${id}-invalid`} role="alert" className="mt-2 text-sm text-red-700">
            {t('message.invalid')}
          </p>
        )}
      </div>
      <div>
        <p className="text-sm font-medium">{t('message.preview')}</p>
        <p
          className="mt-1 whitespace-pre-wrap break-words rounded-md bg-slate-50 p-3 text-sm"
          aria-live="polite"
        >
          {preview || t('message.previewUnavailable')}
        </p>
      </div>
      {failed && (
        <p role="alert" className="text-sm text-red-700">
          {t('message.saveFailed')}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          disabled={saving}
          className="btn-secondary min-h-11"
          onClick={() => {
            setDraft(defaultMessageTemplate(draft.language, sender));
            setFailed(false);
          }}
        >
          {t('message.reset')}
        </button>
        <button
          type="button"
          disabled={saving}
          className="btn-secondary min-h-11"
          onClick={onCancel}
        >
          {t('message.cancel')}
        </button>
        <button type="submit" disabled={!valid || saving} className="btn-primary min-h-11">
          {t(saving ? 'message.saving' : 'message.save')}
        </button>
      </div>
    </form>
  );
}
